import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

interface ServiceCard {
  id: string;
  service_key: string;
  title: string | null;
  description: string | null;
  image_url: string | null;
  active: boolean;
}

const ServicesOverview = () => {
  const { t } = useTranslation();
  const [serviceCards, setServiceCards] = useState<ServiceCard[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchServiceCards();
  }, []);

  const fetchServiceCards = async () => {
    try {
      const { data, error } = await supabase
        .from('website_service_cards')
        .select('id, service_key, title, description, image_url, active')
        .eq('active', true)
        .order('display_order')
        .limit(6);

      if (error) throw error;
      setServiceCards(data || []);
    } catch (error) {
      console.error('Error fetching service cards:', error);
    } finally {
      setLoading(false);
    }
  };

  const services = serviceCards.map(card => ({
    id: card.service_key,
    title: card.title || t(`services.${card.service_key}.title`),
    description: card.description || t(`services.${card.service_key}.description`),
    image_url: card.image_url
  }));

  return (
    <section id="services" className="section bg-white">
      <div className="container">
        <div className="text-center mb-16">
          <h2 className="section-title">{t('services.title')}</h2>
          <p className="max-w-3xl mx-auto text-lg text-gray-600">
            {t('services.subtitle')}
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service) => (
            <div
              key={service.id}
              className="relative h-80 rounded-lg overflow-hidden group shadow-lg hover:shadow-2xl transition-all duration-300"
            >
              {service.image_url && (
                <div
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                  style={{ backgroundImage: `url(${service.image_url})` }}
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-black/30 group-hover:from-black/90 group-hover:via-black/60 transition-all duration-300" />

              <div className="relative h-full flex flex-col justify-end p-6 text-white">
                <h3 className="text-2xl font-bold mb-3 group-hover:text-primary-300 transition-colors duration-300">
                  {service.title}
                </h3>
                <p className="text-gray-100 mb-4 line-clamp-3">
                  {service.description}
                </p>
                <Link
                  to={`/services#${service.id}`}
                  className="text-white font-medium inline-flex items-center border-b-2 border-primary-400 hover:border-primary-300 pb-1 w-fit transition-colors duration-300"
                >
                  Learn more <span className="ml-2 transition-transform duration-300 group-hover:translate-x-1">→</span>
                </Link>
              </div>
            </div>
          ))}
        </div>
        )}

        {!loading && (
          <div className="text-center mt-12">
            <Link to="/services" className="btn btn-outline">
              {t('services.viewAll')}
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default ServicesOverview;